// The clock, outside the page. Oct 3 2026 (Randy, audit §6.7).
// Stripe awards an unanswered dispute to the cardholder, so a deadline must not
// live only at the bottom of the Financials tab. This sits at the top of
// Financials, and the same count rides the nav tab as a badge.
import { useEffect, useState } from "react";
import { Warning, Clock } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { fmt$, deadlineInfo, REVIEW_MUTATED } from "@/lib/financialReview";

export const useReviewAlerts = (deps = []) => {
  const [alerts, setAlerts] = useState(null);
  const [tick, setTick] = useState(0);
  // Oct 3 2026 — the badge and this banner live outside the queue, so a
  // resolve / acknowledge / refund has to tell them. One window event, no
  // context plumbing, and nothing to forget when a new mutation is added:
  // every path already calls the panel's reload, which fires it.
  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    window.addEventListener(REVIEW_MUTATED, bump);
    return () => window.removeEventListener(REVIEW_MUTATED, bump);
  }, []);
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const r = await api.get("/admin/financial-review/alerts");
        if (!dead) setAlerts(r.data);
      } catch { /* decorative */ }
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, ...deps]);
  return alerts;
};

export default function FinancialReviewBanner() {
  const alerts = useReviewAlerts([]);
  if (!alerts) return null;
  const dl = deadlineInfo(alerts.soonest_evidence_due_by);
  const urgent = dl && (dl.tone === "over" || (alerts.evidence_hours_left ?? 999) <= 48);
  const decisions = alerts.bands?.decision?.count || 0;
  if (!urgent && !decisions && !alerts.replays_pending) return null;

  return (
    <div
      data-testid="financial-review-banner"
      className={`mb-6 rounded-lg border px-4 py-3 flex items-start gap-3 ${
        urgent ? "border-[#C16E5A] bg-[#FDF3F0]" : "border-[#D9A97A] bg-[#FDF8EE]"}`}
    >
      {urgent ? <Clock size={16} weight="duotone" className="text-[#7A3A2A] shrink-0 mt-0.5" />
              : <Warning size={16} weight="duotone" className="text-[#8A5A00] shrink-0 mt-0.5" />}
      <div className="min-w-0 text-[11px] leading-relaxed">
        {urgent && (
          <div className="text-[#7A3A2A] font-medium" data-testid="financial-review-banner-deadline">
            {dl.label}.
            <span className="block font-normal text-[#3A3936]">
              Nothing in this app can answer a bank, and an unanswered dispute is lost by default.
            </span>
          </div>
        )}
        {decisions > 0 && (
          <div className="text-[#3A3936]" data-testid="financial-review-banner-decisions">
            {decisions} item{decisions === 1 ? "" : "s"} waiting on your decision
            {alerts.bands.decision.cents
              ? ` · ${fmt$(alerts.bands.decision.cents)} on the table` : ""}.
          </div>
        )}
        {alerts.replays_pending > 0 && (
          <div className="text-[#3A3936]" data-testid="financial-review-banner-replays">
            {alerts.replays_pending} webhook operation(s) failed and are waiting to be re-run.
          </div>
        )}
        <a href="#financials-voucher-refunds"
           data-testid="financial-review-banner-jump"
           className="inline-block mt-1 text-[10px] uppercase tracking-[0.16em] text-[#6B6A68] hover:text-[#1C1B1A]">
          Go to refunds, disputes &amp; review ↓
        </a>
      </div>
    </div>
  );
}
